import DropDownOnSearch from "@routes/intel/ProductOnboarding/DropDownOnSearch";
import { captureException } from "@sentry/react";
import { getApiUrl } from "@utils/baseUrl";
import { getTitleCase, useDebounce } from "@utils/utils";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { updateOnboardingStatus } from "../../../routes/intel/onboarding-screens/onboarding/utils";
import { AUTOSUGGEST_API_MAP, BRANCHES, LINE_OF_BUSINESS_MAP, STEP_FIELD_MAPS } from "../../../routes/intel/ProductOnboarding/utils";

const ModifyInputs = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [formValues, setFormValues] = useState({});
  const [loader, setLoader] = useState(true);
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [dropDownData, setDropDownData] = useState([]);
  const [input, setInput] = useState({});
  const [activeFieldKey, setActiveFieldKey] = useState("");
  const [suggestedData, setSuggestedData] = useState({});
  const [checkedOptions, setCheckedOptions] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const debouncedInputValue = useDebounce(input, 300);
  const lineOfBusiness = formValues.line_of_business?.label;
  const subBranchKey = LINE_OF_BUSINESS_MAP[lineOfBusiness] || "";

  // Disable Line of Business only if user has existing onboarding data (pre-filled from API)
  // This allows first-time users to select their Line of Business freely
  const getData = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("user_onboarding/"));
      const data = res.data.onboarding_data;

      if (data && typeof data === "object" && data !== null) {
        const defaultFormValues = {};
        const defaultSuggestedData = {};

        Object.keys(data).forEach((key) => {
          const item = data[key];
          if (item?.responses) {
            const answers = item.responses;
            Object.keys(answers).forEach((field) => {
              defaultFormValues[field] = answers[field];
              defaultSuggestedData[field] = answers[field];
            });
          } else {
            // Handle nested data (like BUSINESS_INFO_1 with line of business branches)
            if (item && typeof item === "object") {
              Object.keys(item).forEach((subKey) => {
                const subItem = item[subKey];
                if (subItem?.responses) {
                  const answers = subItem.responses;
                  Object.keys(answers).forEach((field) => {
                    defaultFormValues[field] = answers[field];
                    defaultSuggestedData[field] = answers[field];
                  });
                }
              });
            }
          }
        });
        setFormData(data);
        setFormValues(defaultFormValues);
        setSuggestedData(defaultSuggestedData);

        // Initialize checkedOptions from existing selections so dropdown shows checked state
        const initialChecked = {};
        Object.keys(defaultFormValues).forEach((fieldKey) => {
          const value = defaultFormValues[fieldKey];
          if (Array.isArray(value)) {
            const fieldChecked = {};
            value.forEach((item) => {
              const label = item?.description ?? item?.label ?? item;
              if (label && typeof label === "string") {
                fieldChecked[label] = true;
              }
            });
            if (Object.keys(fieldChecked).length > 0) {
              initialChecked[fieldKey] = fieldChecked;
            }
          }
        });
        setCheckedOptions(initialChecked);
      }
    } catch (error) {
      captureException(error);
      toast.error("Failed to load your inputs");
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!activeFieldKey) return;

      const dropdownElement = document.getElementById("suggestions-dropdown");

      if (dropdownElement && !dropdownElement.contains(e.target)) {
        setDropDownOpen(false);
        setIsLoadingSuggestions(false);
        if (input[activeFieldKey] === "") {
          setDropDownData([]);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropDownOpen, activeFieldKey, input]);

  useEffect(() => {
    if (!activeFieldKey || !Array.isArray(suggestedData[activeFieldKey])) return;

    const loadingProductItems = suggestedData[activeFieldKey].filter((item) => item?.from === "product_description" && item?.isloading === true);
    if (loadingProductItems.length === 0) return;

    const fetchHSCodesForItem = async (item) => {
      try {
        const response = await axios.post(getApiUrl("postSearchRecomendation"), {
          user_search: item.key,
          category: "product",
        });

        const hsInfo = response.data?.hs_info || {};
        const userSearch = response.data?.user_search || item.key;

        const updatedItem = {
          key: hsInfo.hs_code || item.key,
          description: getTitleCase(userSearch),
          issuggested: false,
          isloading: false,
          from: "product_description",
          company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
          isBookmarked: true,
        };

        setSuggestedData((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = Array.isArray(currentList) && currentList?.length > 0 ? currentList.map((el) => (el.key === item.key ? updatedItem : el)) : [];
          return { ...prev, [activeFieldKey]: updatedList };
        });
        setFormValues((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = Array.isArray(currentList) && currentList?.length > 0 ? currentList.map((el) => (el.key === item.key ? updatedItem : el)) : [];
          return { ...prev, [activeFieldKey]: updatedList };
        });
      } catch (error) {
        setSuggestedData((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = currentList.map((el) => (el.key === item.key ? { ...el, isloading: false, error: true } : el));
          return { ...prev, [activeFieldKey]: updatedList };
        });
        setFormValues((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = currentList.map((el) => (el.key === item.key ? null : el));
          return { ...prev, [activeFieldKey]: updatedList };
        });
      }
    };

    loadingProductItems.forEach((item) => fetchHSCodesForItem(item));
  }, [suggestedData, activeFieldKey]);

  // Debouncing implementation for autosuggestions
  useEffect(() => {
    const identifier = activeFieldKey;
    const value = debouncedInputValue[identifier]?.trim() ?? "";

    if (!identifier) return;

    // Find current step and field for API configuration
    let currentStepKey = null;
    let apiConfig = null;

    // Determine which step this field belongs to
    Object.keys(STEP_FIELD_MAPS).forEach((stepKey) => {
      const fields = STEP_FIELD_MAPS[stepKey];
      if (Array.isArray(fields) && fields.includes(identifier)) {
        currentStepKey = stepKey;
      } else if (typeof fields === "object" && subBranchKey) {
        const branchFields = fields[subBranchKey] || [];
        if (branchFields.includes(identifier)) {
          currentStepKey = stepKey;
        }
      }
    });

    if (currentStepKey) {
      apiConfig = AUTOSUGGEST_API_MAP[currentStepKey]?.[subBranchKey]?.[identifier];
    }

    if (!apiConfig || typeof apiConfig.api !== "function") {
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setIsLoadingSuggestions(true);
        const url = getApiUrl(apiConfig.api(value));
        const res = await axios.get(url);

        let autoSuggestResponses = [];
        const keysToProcess = identifier === "bd_ports" ? ["ports"] : Object.keys(res.data);
        keysToProcess.forEach((key) => {
          const dataSource = identifier === "bd_ports" ? res.data?.ports : res.data[key];
          if (Array.isArray(dataSource) && dataSource.length > 0) {
            autoSuggestResponses.push(
              ...dataSource.map((item) => {
                if (typeof item === "object" && item !== null) {
                  return {
                    ...item,
                    from: key,
                    doc_count: item?.value ?? "0",
                    company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
                    isBookmarked: true,
                  };
                }
                return {
                  value: item,
                  label: String(item),
                  from: key,
                  doc_count: item?.value ?? "0",
                  company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
                  isBookmarked: true,
                };
              })
            );
          }
        });
        setDropDownData(autoSuggestResponses);
        setDropDownOpen(true);
      } catch (error) {
        setDropDownOpen(false);
      } finally {
        setIsLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [debouncedInputValue, activeFieldKey, subBranchKey]);

  const handleInputChange = (identifier, value, apiCallReq = true, inputType = "") => {
    setValidationErrors((prev) => ({ ...prev, [identifier]: "" }));
    if (apiCallReq) {
      setInput({ [identifier]: value });
      setActiveFieldKey(identifier);
    } else if (!apiCallReq && identifier !== "line_of_business") {
      setInput((prev) => ({ ...prev, [identifier]: value }));
      setDropDownOpen(true);
    } else if (identifier === "line_of_business") {
      setDropDownOpen(true);
      setFormValues((prev) => ({
        ...prev,
        [identifier]: { textInput: "", label: value, checked: true },
      }));
    }
    if (inputType === "radio") {
      setDropDownOpen(false);
      setActiveFieldKey("");
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoader(true);
      setValidationErrors({});

      // Validate only fields where isMandatory is true
      const validationErrors = {};
      let hasError = false;

      const collectMandatoryFields = () => {
        const mandatoryItems = [];
        const sectionKeys = ["PERSONA_INFO", "LINE_OF_BUSINESS_INFO_1", "BUSINESS_INFO_1", "LINE_OF_BUSINESS_INFO_2", "BUSINESS_INFO_2"];
        sectionKeys.forEach((sectionKey) => {
          let sectionData = formData[sectionKey];
          if (!sectionData) return;
          if (BRANCHES[sectionKey] && subBranchKey) {
            sectionData = sectionData[subBranchKey];
          }
          const sections = sectionData?.section || [];
          sections.forEach((item) => {
            if (item.isMandatory) {
              mandatoryItems.push(item);
            }
          });
        });
        return mandatoryItems;
      };

      const mandatoryItems = collectMandatoryFields();
      mandatoryItems.forEach((item) => {
        const fieldValue = formValues[item.identifier];
        let isEmpty = false;

        if (item.inputType === "multitext" || item.inputType === "checkbox") {
          const currentItems = suggestedData[item.identifier] || [];
          const nonSuggestedItems = currentItems.filter((i) => !i.issuggested);
          isEmpty = nonSuggestedItems.length === 0;
        } else if (item.inputType === "radio") {
          isEmpty = !fieldValue?.label;
        } else {
          isEmpty = !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
        }

        if (isEmpty) {
          validationErrors[item.identifier] = "This field is required";
          hasError = true;
        }
      });

      if (hasError) {
        setValidationErrors(validationErrors);
        toast.error("Please fill all required fields");
        return;
      }

      // API call to save all onboarding data
      const response = await axios.post(getApiUrl("user_onboarding/"), {
        input_details: formValues,
      });

      if (response.data.status === "success") {
        toast.success("Your inputs have been updated successfully");
        updateOnboardingStatus("show_onboarding_popup", false);
        updateOnboardingStatus("persona_updated_by_script", false);
        onClose();
        onSuccess();
      } else {
        toast.error("Failed to save changes");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = () => {
    // Reset all form state and close
    setFormValues({});
    setSuggestedData({});
    setCheckedOptions({});
    setInput({});
    setActiveFieldKey("");
    setDropDownData([]);
    setDropDownOpen(false);
    setValidationErrors({});
    onClose();
  };

  const renderFormSection = (sectionKey, sectionData) => {
    if (!sectionData) return null;

    // Exclude FOUNDATIONAL_INFO
    if (sectionKey === "FOUNDATIONAL_INFO") return null;

    // Handle sections that are branched by line of business
    if (BRANCHES[sectionKey] && subBranchKey) {
      const branchedData = sectionData[subBranchKey];
      if (!branchedData) return null;
      return branchedData.section?.map((section) => renderFormField(section));
    }

    // Handle regular sections
    if (sectionData.section) {
      return (
        <div key={sectionKey} className="tw-mb-4 tw-border-b tw-border-gray-200 tw-pb-4">
          {sectionData.section.map((section) => renderFormField(section))}
        </div>
      );
    }

    return null;
  };

  const renderFormField = (section) => {
    const fieldValue = formValues[section.identifier];
    const error = validationErrors[section.identifier];

    if (section.identifier === "line_of_business") {
      return (
        <div key={section.identifier} className="tw-text-sm ">
          <span className="tw-font-medium">Line of Business: </span> {fieldValue?.label || ""}
        </div>
      );
    }
    if (section.inputType === "radio") {
      return (
        <div key={section.identifier} className="tw-flex tw-flex-col tw-gap-2">
          <span className="tw-text-sm tw-font-semibold">
            {section.title} {section.isMandatory ? "*" : ""}
          </span>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {section.values.map((el) => {
              const hasValue = fieldValue?.label === el.value;
              return (
                <label key={el.value} className={`tw-flex tw-items-center tw-gap-2 tw-py-2 tw-mb-0 tw-px-3 tw-border tw-rounded-lg tw-cursor-pointer ${hasValue ? "" : "tw-bg-white hover:tw-bg-gray-50"}`}>
                  <input
                    type="radio"
                    name={section.identifier}
                    value={el.value}
                    checked={hasValue}
                    className="disabled:!tw-cursor-not-allowed disabled:!tw-accent-primary-purple-200 !tw-accent-primary-purple-600"
                    onChange={(e) => {
                      handleInputChange(section.identifier, e.target.value, false, section.inputType);
                      setFormValues((prev) => ({
                        ...prev,
                        [section.identifier]: { textInput: "", label: e.target.value, checked: true },
                      }));
                    }}
                  />
                  <span className="tw-text-sm tw-font-normal tw-text-gray-700">{el.value}</span>
                </label>
              );
            })}
          </div>
          {error && <span className="tw-text-red-500 tw-text-xs">{error}</span>}
        </div>
      );
    }

    if (section.inputType === "multitext" || section.inputType === "checkbox") {
      const currentItems = suggestedData[section.identifier] || [];
      const nonSuggestedItems = currentItems.filter((item) => !item.issuggested);

      return (
        <div key={section.identifier} className="tw-flex tw-flex-col tw-gap-2 tw-mb-4" data-field={section.identifier}>
          <span className="tw-text-sm tw-font-semibold">
            {section.title} {section.isMandatory ? "*" : ""}
          </span>
          <div className="tw-relative tw-w-full tw-min-h-[38px] tw-py-1.5 tw-px-2 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-gray-50 tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
            <input
              onClick={() => {
                setActiveFieldKey(section.identifier);
                setDropDownOpen(true);
              }}
              onChange={(e) => handleInputChange(section.identifier, e.target.value, true, section.inputType)}
              value={input[section.identifier] || ""}
              type="text"
              placeholder={section.placeholder || `Type to search ${section.title.toLowerCase()}`}
              className="tw-flex-1 tw-min-w-[120px] tw-border-0 tw-outline-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium"
            />
          </div>
          {dropDownOpen && activeFieldKey === section.identifier && (
            <div className="tw-flex tw-text-sm tw-font-medium tw-relative">
              <DropDownOnSearch isLoadingSuggestions={isLoadingSuggestions} data={section.values || dropDownData} setSuggestedData={setSuggestedData} setInput={setInput} setDropDownOpen={setDropDownOpen} currentFieldKey={section.identifier} setFormValues={setFormValues} setCheckedOptions={setCheckedOptions} checkedOptions={checkedOptions} dropDownInputType={section.inputType} searchInput={input[section.identifier] || ""} formValues={formValues} setDropDownData={setDropDownData} />
            </div>
          )}
          {nonSuggestedItems.length > 0 && (
            <div className="tw-mt-2">
              {["exp_imp_products_hscodes_exp", "exp_imp_products_hscodes_imp"].includes(section.identifier) && <div className="tw-text-xs tw-text-gray-500 tw-mb-2">Our AI classifier has mapped following HS Codes with products to give you better results</div>}
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {nonSuggestedItems.map((item, idx) => (
                  <div key={item.company_id || item.key || `${item.description}-${idx}`} className="tw-flex tw-items-center tw-gap-2 tw-bg-primary-purple-50 tw-rounded-full tw-px-3 tw-py-1">
                    <span className="tw-text-xs tw-text-primary-purple-600 tw-capitalize">
                      {item.description}
                      {item.key && item.key !== item.description && <span className="tw-text-xs tw-text-gray-600 tw-ml-1">(HS: {item.key})</span>}
                    </span>
                    <button
                      type="button"
                      className="tw-flex tw-items-center tw-h-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedItems = nonSuggestedItems.filter((_, i) => i !== idx);
                        setSuggestedData((prev) => ({ ...prev, [section.identifier]: updatedItems }));
                        setFormValues((prev) => ({ ...prev, [section.identifier]: updatedItems }));
                        setCheckedOptions((prev) => ({
                          ...prev,
                          [section.identifier]: {
                            ...prev[section.identifier],
                            [item.description]: false,
                          },
                        }));
                      }}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#5a1ce2">
                        <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && <span className="tw-text-red-500 tw-text-xs">{error}</span>}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="tw-bg-gray-white">
      <div className="tw-h-[calc(100vh-115px)] tw-overflow-y-auto">
        <div className="tw-text-center tw-py-10">
          <h1 className="tw-text-3xl tw-font-medium tw-text-gray-900 tw-mb-1.5">Modify Your Inputs</h1>
          <p className="tw-text-sm tw-text-gray-500">Update your preferences to refine recommendations</p>
        </div>

        <div>
          {loader ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-primary-purple-600"></div>
            </div>
          ) : (
            <div className="tw-max-w-[850px] tw-mx-auto">
              <div className="tw-mx-5 tw-mb-4">
                {formData["PERSONA_INFO"] && renderFormSection("PERSONA_INFO", formData["PERSONA_INFO"])}
                <div className="tw-py-2 tw-mb-4 tw-px-3 tw-bg-[#fff3e9] tw-rounded-lg tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#dc6803">
                    <path d="M444-288h72v-240h-72v240Zm61.5-322.29q10.5-10.29 10.5-25.5t-10.29-25.71q-10.29-10.5-25.5-10.5t-25.71 10.29q-10.5 10.29-10.5 25.5t10.29 25.71q10.29 10.5 25.5 10.5t25.71-10.29ZM480.28-96Q401-96 331-126t-122.5-82.5Q156-261 126-330.96t-30-149.5Q96-560 126-629.5q30-69.5 82.5-122T330.96-834q69.96-30 149.5-30t149.04 30q69.5 30 122 82.5T834-629.28q30 69.73 30 149Q864-401 834-331t-82.5 122.5Q699-156 629.28-126q-69.73 30-149 30Zm-.28-72q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z" />
                  </svg>
                  Changes to mandatory fields will directly impact the recommendations and insights shown in your AI Prospecting
                </div>
                {lineOfBusiness && (
                  <>
                    {formData["LINE_OF_BUSINESS_INFO_1"] && renderFormSection("LINE_OF_BUSINESS_INFO_1", formData["LINE_OF_BUSINESS_INFO_1"])}
                    {formData["BUSINESS_INFO_1"] && renderFormSection("BUSINESS_INFO_1", formData["BUSINESS_INFO_1"])}
                    {formData["LINE_OF_BUSINESS_INFO_2"] && renderFormSection("LINE_OF_BUSINESS_INFO_2", formData["LINE_OF_BUSINESS_INFO_2"])}
                    {formData["BUSINESS_INFO_2"] && renderFormSection("BUSINESS_INFO_2", formData["BUSINESS_INFO_2"])}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="tw-p-3 tw-border-t tw-border-gray-200 tw-flex tw-justify-between tw-bg-white tw-max-w-[850px] tw-mx-auto">
        <button onClick={handleCancel} className="tw-px-6 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-text-gray-700 tw-rounded-lg tw-font-medium hover:tw-bg-gray-50">
          Go Back
        </button>
        <button onClick={handleSaveChanges} disabled={loader} className="tw-px-6 tw-py-2 tw-bg-primary-purple-600 tw-text-white tw-rounded-lg tw-font-medium hover:tw-bg-primary-purple-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default ModifyInputs;
